import { useEffect, useMemo, useRef, useState } from 'react'
import { FiLogOut, FiSettings, FiUser } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { useUserRealtimeData } from '../hooks/useUserRealtimeData'
import { useNavigate } from 'react-router-dom'
import { asaasStatusPtbr } from '../utils/asaasStatusPtbr'
import { planDisplayName, normalizePlanId } from '../utils/subscriptionPlan'

const ProfileMenu = () => {
  const { user, logout } = useAuth()
  const { profile, plan } = useUserRealtimeData()
  const [open, setOpen] = useState(false)
  const btnRef = useRef(null)
  const panelRef = useRef(null)
  const navigate = useNavigate()

  const initials = useMemo(() => {
    const name = user?.displayName || user?.email || ''
    const parts = name.split(' ').filter(Boolean)
    const a = parts[0]?.[0] || 'U'
    const b = parts.length > 1 ? parts[parts.length - 1][0] : ''
    return (a + b).toUpperCase()
  }, [user])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onClick = (e) => {
      const t = e.target
      if (panelRef.current?.contains(t) || btnRef.current?.contains(t)) return
      setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onClick)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onClick)
    }
  }, [open])

  if (!user) return null

  const planLabel = plan?.planName || planDisplayName(plan?.planId)
  const isFreePlan = normalizePlanId(plan?.planId) === 'gratuito'
  const statusLabel = isFreePlan ? null : asaasStatusPtbr(plan?.status) || null
  const photoUrl = user.photoURL || profile?.photoURL || null
  const [photoError, setPhotoError] = useState(false)

  return (
    <>
      <div className="relative">
        <button
          ref={btnRef}
          onClick={() => setOpen((v) => !v)}
          className="h-[44px] px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-accent dark:hover:border-accent transition-all duration-200 flex items-center gap-3 focus:outline-none overflow-hidden"
          aria-label="Abrir menu do perfil"
          aria-expanded={open}
        >
          <span className="h-9 w-9 rounded-xl bg-accent/10 dark:bg-accent/20 flex items-center justify-center overflow-hidden flex-shrink-0">
            {photoUrl && !photoError ? (
              <img
                src={photoUrl}
                alt=""
                className="h-full w-full object-cover"
                referrerPolicy="no-referrer"
                onError={() => setPhotoError(true)}
              />
            ) : (
              <span className="font-extrabold text-sm text-accent">{initials}</span>
            )}
          </span>
          <span className="hidden xl:block text-left min-w-0">
            <span className="block text-sm font-extrabold text-gray-900 dark:text-white truncate">
              {user.displayName || profile?.displayName || 'Minha conta'}
            </span>
            <span className="block text-xs text-gray-600 dark:text-gray-400 truncate">
              {planLabel ? `${planLabel}${statusLabel ? ` • ${statusLabel}` : ''}` : (user.email || '')}
            </span>
          </span>
        </button>

        {open && (
          <div
            ref={panelRef}
            className="absolute right-0 mt-2 w-80 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden z-[70]"
            role="menu"
          >
            <div className="p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-accent/10 dark:bg-accent/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {photoUrl && !photoError ? (
                    <img
                      src={photoUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      referrerPolicy="no-referrer"
                      onError={() => setPhotoError(true)}
                    />
                  ) : (
                    <FiUser className="w-5 h-5 text-accent" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-gray-900 dark:text-white truncate">
                    {user.displayName || profile?.displayName || 'Minha conta'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                    {user.email}
                  </p>
                </div>
              </div>

              <div className={`grid gap-2 mt-4 ${isFreePlan ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 p-3">
                  <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-[0.2em]">Plano</p>
                  <p className="mt-1 text-sm font-extrabold text-gray-900 dark:text-white truncate">
                    {planLabel || '—'}
                  </p>
                </div>
                {!isFreePlan && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 p-3">
                  <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-[0.2em]">Status</p>
                  <p className="mt-1 text-sm font-extrabold text-gray-900 dark:text-white truncate">
                    {statusLabel || '—'}
                  </p>
                </div>
                )}
              </div>
            </div>

            <div className="p-2">
              <button
                onClick={() => {
                  navigate('/account')
                  setOpen(false)
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                role="menuitem"
              >
                <FiSettings className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                Minha conta
              </button>

              <button
                onClick={async () => {
                  await logout()
                  setOpen(false)
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                role="menuitem"
              >
                <FiLogOut className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                Sair
              </button>
            </div>
          </div>
        )}
      </div>

    </>
  )
}

export default ProfileMenu

